'use client';

import { useCallback, useContext } from 'react';
import type { EdgeCheckoutMode } from '@benzinga/ads-utils';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { buildCheckoutEventProperties, createCheckoutSessionId } from '../checkoutTracking';
import type { EdgeIframeCheckoutParams } from '../buildEdgeIframeCheckoutUrl';
import { openEdgeCheckout } from '../edgeCheckoutController';

type UseEdgePaywallCheckoutModalOptions = {
  checkoutMode: EdgeCheckoutMode;
  checkoutParams: EdgeIframeCheckoutParams;
  checkoutType: string;
  checkoutUrl: string;
  isCheckoutModeReady: boolean;
  paywallId: string;
  placement?: string;
  setShowPaywall?: (show: boolean) => void;
};

export const useEdgePaywallCheckoutModal = ({
  checkoutMode,
  checkoutParams,
  checkoutType,
  checkoutUrl,
  isCheckoutModeReady,
  paywallId,
  placement,
  setShowPaywall,
}: UseEdgePaywallCheckoutModalOptions) => {
  const session = useContext(SessionContext);
  const usesModalCheckout = isCheckoutModeReady && checkoutMode === 'modal';

  const handleCheckoutClick = useCallback(() => {
    if (!isCheckoutModeReady) {
      return;
    }

    const checkoutSessionId = createCheckoutSessionId();
    const checkoutModeValue = usesModalCheckout ? 'modal' : 'new_tab';
    const trackingSource = { checkoutParams, checkoutType, paywallId, placement };

    session.getManager(TrackingManager).trackCheckoutEvent(
      'click',
      buildCheckoutEventProperties(trackingSource, {
        checkout_mode: checkoutModeValue,
        checkout_session_id: checkoutSessionId,
      }),
    );

    if (!usesModalCheckout) {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    openEdgeCheckout({
      checkoutParams,
      checkoutSessionId,
      checkoutType,
      onUnlock: setShowPaywall ? () => setShowPaywall(false) : undefined,
      paywallId,
      placement,
    });
  }, [
    checkoutParams,
    checkoutType,
    checkoutUrl,
    isCheckoutModeReady,
    paywallId,
    placement,
    session,
    setShowPaywall,
    usesModalCheckout,
  ]);

  return {
    checkoutMode,
    handleCheckoutClick,
    usesModalCheckout,
  };
};
